import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { ConfigProvider, Segmented, Tooltip } from 'antd';
import BetaTag from 'components/common/BetaTag/BetaTag';
import { FARMS_URL_PARAM_PLANNING_LENS } from 'constants/routeConstants';
import {
  INPUT_DOC_LENS_NUTRITION,
  INPUT_DOC_LENS_ROTATION,
  PLANNING_LENS_LABELS,
} from 'constants/uiConstants';
import {
  getPlanningLensUnavailableReason,
  PLANNING_LENS_ORDER,
  type PlanningLens,
} from 'pages/FarmsPage/hooks/resolvePlanningLens';

import type { InsightsScopeLevel } from './Insights/insightUtils';

// Same lens switcher as Insights and Farm diagnostics: the
// .insights-lens-segmented rules in App.css style the selected label. var()
// references, not resolved values — see Insights/index.tsx for why.
const colorDarkGray = 'var(--color-dark-gray)';
const colorWhite = 'var(--color-white)';

const BETA_LENSES = new Set<PlanningLens>([
  INPUT_DOC_LENS_ROTATION,
  INPUT_DOC_LENS_NUTRITION,
]);

interface PlanningLensSwitcherProps {
  scopeLevel: InsightsScopeLevel | undefined;
  /** Null when no lens applies (a crop node) — nothing is selected. */
  value: PlanningLens | null;
}

/**
 * Switches Planning between Rotation, Paddock plan and Nutrition (OP-2549),
 * and Paddocks for managing farms and paddocks (OP-2481).
 * All three always show, in the same order, so the tools stay where users
 * expect them; one that doesn't apply to the tree selection is greyed out
 * with a tooltip saying what to select. The lens lives in the URL (`plens`),
 * so it's kept when the tree selection changes and a link can open it.
 */
const PlanningLensSwitcher: React.FC<PlanningLensSwitcherProps> = ({
  scopeLevel,
  value,
}) => {
  const [, setSearchParams] = useSearchParams();

  return (
    <ConfigProvider
      theme={{
        components: {
          Segmented: {
            trackBg: 'transparent',
            trackPadding: 2,
            itemSelectedBg: colorDarkGray,
            itemSelectedColor: colorWhite,
          },
        },
      }}
    >
      <Segmented<PlanningLens>
        className="insights-lens-segmented w-fit"
        options={PLANNING_LENS_ORDER.map((lens) => {
          const unavailableReason = getPlanningLensUnavailableReason(
            lens,
            scopeLevel
          );
          return {
            value: lens,
            disabled: !!unavailableReason,
            label: (
              <Tooltip title={unavailableReason}>
                <span>
                  {PLANNING_LENS_LABELS[lens]}
                  {BETA_LENSES.has(lens) && <BetaTag />}
                </span>
              </Tooltip>
            ),
          };
        })}
        value={value ?? undefined}
        onChange={(lens) =>
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set(FARMS_URL_PARAM_PLANNING_LENS, lens);
            return next;
          })
        }
      />
    </ConfigProvider>
  );
};

export default PlanningLensSwitcher;
